/**
 * Jira-style log duration: decimal hours with `h` (e.g. 2.5h, .25h) and integer minutes with `m` (e.g. 15m).
 * API continues to receive total minutes; this module parses input and formats hours like Jira (e.g. 1m → 0.02h).
 */

/** Log time entries must be strictly under 24 hours (same cap in UI validation and API payload). */
export const MAX_LOG_DURATION_MINUTES_EXCLUSIVE = 24 * 60;

const TOKEN_SPLIT = /(\d+(?:\.\d+)?h)(\d+m)/gi;

function normalizeDurationSpaces(str) {
  let s = String(str ?? "")
    .trim()
    .replace(/,/g, " ")
    .replace(/\s+/g, " ");
  let prev;
  do {
    prev = s;
    s = s.replace(TOKEN_SPLIT, "$1 $2");
  } while (s !== prev);
  return s.trim();
}

/**
 * @param {unknown} input
 * @returns {{ ok: true, minutes: number } | { ok: false, minutes: 0, error: string }}
 */
export function parseJiraLogDurationToMinutes(input) {
  const fail = (error) => ({ ok: false, minutes: 0, error });

  if (input == null || String(input).trim() === "") {
    return fail("Duration is required.");
  }

  const s = normalizeDurationSpaces(input);
  if (!s) return fail("Duration is required.");

  if (/^0+\s*h?$/i.test(s) || s === "0" || s === "0h" || s === "0m") {
    return fail("Enter a positive duration.");
  }

  const tokens = s.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return fail("Duration is required.");

  let total = 0;
  for (const tok of tokens) {
    const h = tok.match(/^(\d+\.?\d*|\.\d+)h$/i);
    const m = tok.match(/^(\d+)m$/i);
    if (h) {
      const n = Number(h[1]);
      if (!Number.isFinite(n) || n < 0) return fail(`Invalid hours in "${tok}".`);
      total += n * 60;
      continue;
    }
    if (m) {
      const n = Number(m[1]);
      if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
        return fail(`Minutes in "${tok}" must be a whole number.`);
      }
      total += n;
      continue;
    }
    return fail(
      `Invalid segment "${tok}". Use decimal hours (2.5h, .25h) and/or whole minutes (15m), e.g. 1h 30m.`
    );
  }

  const rounded = Math.round(total);
  if (!Number.isFinite(rounded) || rounded <= 0) {
    return fail("Enter a positive duration.");
  }

  if (rounded >= MAX_LOG_DURATION_MINUTES_EXCLUSIVE) {
    return fail("Duration must be less than 24 hours.");
  }

  return { ok: true, minutes: rounded };
}

/**
 * Jira-style hours display (2 decimal places, trimmed trailing zeros), with `h` suffix.
 * @param {number} minutes
 * @returns {string | null} e.g. "0.02h" for 1 minute, "0.25h" for 15 minutes; null if not positive.
 */
export function minutesToJiraHoursDisplay(minutes) {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) return null;
  const hours = n / 60;
  const fixed = hours.toFixed(2);
  const trimmed = fixed.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
  return `${trimmed}h`;
}

/** Prefill duration input when editing a log (same rules as Jira hours display). */
export function minutesToPrefillLogDuration(minutes) {
  const d = minutesToJiraHoursDisplay(minutes);
  return d ?? "0h";
}

/**
 * Parse billable duration; must be positive and not exceed logged minutes (Jira logged vs billable).
 * @param {unknown} input
 * @param {number} maxMinutesLogged
 */
export function parseBillableDurationToMinutes(input, maxMinutesLogged) {
  const s = normalizeDurationSpaces(input);
  if (!s || s === "0" || s === "0h" || s === "0m" || /^0+\s*h?$/i.test(s)) {
    return { ok: true, minutes: 0 };
  }

  const parsed = parseJiraLogDurationToMinutes(input);
  if (!parsed.ok) return parsed;
  const cap = Number(maxMinutesLogged);
  if (Number.isFinite(cap) && cap >= 0 && parsed.minutes > cap) {
    return {
      ok: false,
      minutes: 0,
      error: "Billable time cannot exceed logged time.",
    };
  }
  return parsed;
}
